import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { MaintenanceSite, SiteSource } from '../types'

// SSO response types
interface SsoResponse {
  login_url: string
}

/**
 * TanStack Query hooks for the Universal Sites Registry
 *
 * Provides data fetching for the central WordPress sites registry.
 */

// Base URL for API requests
const API_BASE = '/api/maintenance'

// Query keys for cache management
export const sitesRegistryKeys = {
  all: ['sites-registry'] as const,
  sites: (params?: SitesQueryParams) => [...sitesRegistryKeys.all, 'sites', params] as const,
  site: (id: string) => [...sitesRegistryKeys.all, 'site', id] as const,
  stats: () => [...sitesRegistryKeys.all, 'stats'] as const,
}

// Query parameters for sites list
export interface SitesQueryParams {
  page?: number
  limit?: number
  search?: string
  source?: SiteSource | SiteSource[]
  status?: 'active' | 'inactive' | 'archived'
  sort?: 'domain' | 'name' | 'createdAt'
  order?: 'asc' | 'desc'
}

// Sites list response
export interface SitesListResponse {
  sites: MaintenanceSite[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// Sites stats response
export interface SitesStatsResponse {
  total: number
  withJezpress: number
  onRocket: number
  bySource: Record<string, number>
  byStatus: Record<string, number>
}

// Create site input
export interface CreateSiteInput {
  domain: string
  name?: string
  source?: SiteSource
  notes?: string
  status?: 'active' | 'inactive'
}

interface ErrorResponse {
  error?: string
}

/**
 * Fetch sites with pagination and filters
 */
export function useSitesRegistry(params?: SitesQueryParams) {
  return useQuery({
    queryKey: sitesRegistryKeys.sites(params),
    queryFn: async (): Promise<SitesListResponse> => {
      const searchParams = new URLSearchParams()

      if (params?.page) searchParams.set('page', String(params.page))
      if (params?.limit) searchParams.set('limit', String(params.limit))
      if (params?.search) searchParams.set('search', params.search)
      if (params?.status) searchParams.set('status', params.status)
      if (params?.sort) searchParams.set('sort', params.sort)
      if (params?.order) searchParams.set('order', params.order)

      // Handle source filtering (single or array)
      if (params?.source) {
        const sources = Array.isArray(params.source) ? params.source : [params.source]
        searchParams.set('source', sources.join(','))
      }

      const url = searchParams.toString()
        ? `${API_BASE}/sites?${searchParams.toString()}`
        : `${API_BASE}/sites`

      const response = await fetch(url, { credentials: 'include' })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to fetch sites')
      }

      return response.json()
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Fetch sites stats
 */
export function useSitesStats() {
  return useQuery({
    queryKey: sitesRegistryKeys.stats(),
    queryFn: async (): Promise<SitesStatsResponse> => {
      const response = await fetch(`${API_BASE}/sites/stats`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to fetch sites stats')
      }

      return response.json()
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Fetch single site by ID
 */
export function useSite(id: string) {
  return useQuery({
    queryKey: sitesRegistryKeys.site(id),
    queryFn: async (): Promise<MaintenanceSite> => {
      const response = await fetch(`${API_BASE}/sites/${id}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to fetch site')
      }

      const data = (await response.json()) as { site: MaintenanceSite }
      return data.site
    },
    enabled: !!id,
  })
}

/**
 * Create a new site (manual or external)
 */
export function useCreateSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSiteInput): Promise<MaintenanceSite> => {
      const response = await fetch(`${API_BASE}/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to create site')
      }

      const data = (await response.json()) as { site: MaintenanceSite }
      return data.site
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sitesRegistryKeys.all })
    },
  })
}

/**
 * Update a site's metadata (tags, notes, schedule, status, name)
 */
export function useUpdateSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string
      tags?: string[]
      notes?: string | null
      checkIntervalDays?: number
      nextMaintenanceDue?: number | null
      status?: string
      name?: string
    }): Promise<MaintenanceSite> => {
      const response = await fetch(`${API_BASE}/sites/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to update site')
      }

      const data = (await response.json()) as { site: MaintenanceSite }
      return data.site
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: sitesRegistryKeys.site(variables.id) })
      queryClient.invalidateQueries({ queryKey: sitesRegistryKeys.all })
    },
  })
}

/**
 * Sync sites from external sources
 */
export function useSyncSites() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (source: 'rocketnet' | 'auth-server' | 'all'): Promise<{
      success: boolean
      sitesProcessed: number
      sitesCreated: number
      sitesUpdated: number
    }> => {
      const response = await fetch(`${API_BASE}/sites/sync?source=${source}`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to sync sites')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sitesRegistryKeys.all })
    },
  })
}

/**
 * Get JezPress SSO login URL for a site
 * Uses the auth-server SSO token endpoint
 */
export function useJezPressSso() {
  return useMutation({
    mutationFn: async ({ domain, userEmail }: { domain: string; userEmail: string }): Promise<SsoResponse> => {
      const response = await fetch(`/api/auth-server/sites/${encodeURIComponent(domain)}/sso-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_email: userEmail }),
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to create SSO token')
      }

      return response.json()
    },
  })
}

/**
 * Get Rocket.net WP Login SSO URL for a site
 * Uses the Rocket.net API wp-login endpoint
 */
export function useRocketSso() {
  return useMutation({
    mutationFn: async (rocketSiteId: string): Promise<SsoResponse> => {
      const response = await fetch(`/api/rocketnet/sites/${rocketSiteId}/wp-login`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse
        throw new Error(errorData.error || 'Failed to get login URL')
      }

      return response.json()
    },
  })
}
